import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Instagram } from "lucide-react";
import { twMerge } from "tailwind-merge";
import BrandMark from "./BrandMark";
import { Button } from "./ui";
import { useAuth } from "../lib/auth";
import { hostCtaTarget } from "../lib/hostProfile";
import { useDemoChooser } from "../lib/demoChooserContext";
import { useHostThemePreview } from "../lib/hostThemePreview";
import { hostThemeMeta, normalizeHostTheme } from "../lib/hostThemes";

const linkBase =
  "font-ui font-medium text-[13px] underline underline-offset-4 decoration-current/25 hover:decoration-current/50 transition-colors";

/** Footer nav link — cream on home night band; ink tokens elsewhere. */
function FooterLink({ className = "", isHome, ...rest }) {
  return (
    <Link
      className={twMerge(
        "block",
        linkBase,
        isHome
          ? "text-cream/70 hover:text-cream"
          : "text-[color:var(--ink-secondary,var(--muted))] hover:text-ink",
        className,
      )}
      {...rest}
    />
  );
}

export default function SiteFooter({ variant = "default" }) {
  const loc = useLocation();
  const { account } = useAuth();
  const hostCta = hostCtaTarget(account);
  const { openDemoChooser } = useDemoChooser();
  const { themeId } = useHostThemePreview();
  const isHome = variant === "home";

  if (loc.pathname === "/login") return null;

  // Home footer sits outside HostThemePreviewShell — apply preview theme locally
  // so night/cream/canvas tokens track the logo cycle. Public pages rely on
  // HostThemedShell’s <html> mirror instead.
  const previewTheme = isHome ? normalizeHostTheme(themeId) : undefined;
  const previewDark = previewTheme ? hostThemeMeta(previewTheme).dark : false;

  const mutedLabel = isHome ? "text-cream/50" : "text-muted";
  const bodyCopy = isHome
    ? "text-cream/65"
    : "text-[color:var(--ink-secondary,var(--muted))]";
  const actionClass = twMerge(
    "block text-left",
    linkBase,
    isHome
      ? "text-cream/70 hover:text-cream"
      : "text-[color:var(--ink-secondary,var(--muted))] hover:text-ink",
  );

  return (
    <footer
      className={
        isHome
          ? "site-footer bg-night text-cream"
          : "site-footer bg-canvas border-t border-subtle text-ink"
      }
      data-testid="site-footer"
      data-host-theme={previewTheme}
      data-host-theme-dark={previewDark ? "true" : undefined}
    >
      <div className="max-w-page mx-auto px-4 py-10 grid sm:grid-cols-3 gap-8 items-start">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <BrandMark size="sm" themeId={previewTheme} />
            <span className="font-display font-semibold text-[20px] text-current">
              rsvp
              <span className="site-footer-domain">.dance</span>
            </span>
          </div>
          <p className={twMerge("font-normal text-[14px]", bodyCopy)}>
            Where dancers find their next night out.
          </p>
        </div>
        <div>
          <p className={twMerge("font-ui font-medium text-[13px] mb-2", mutedLabel)}>For hosts</p>
          <div className="flex flex-col gap-1.5">
            <FooterLink to="/for-organizers" isHome={isHome} data-testid="footer-for-organizers-link">
              Grow your scene
            </FooterLink>
            <FooterLink to={hostCta.path} isHome={isHome}>
              {hostCta.label}
            </FooterLink>
            <Button
              type="button"
              variant="link"
              size="sm"
              onClick={openDemoChooser}
              className={actionClass}
              data-testid="footer-try-demo-link"
            >
              Try the demo
            </Button>
            <FooterLink to="/faq" isHome={isHome} data-testid="footer-faq-link">
              FAQ
            </FooterLink>
            <FooterLink to="/support" isHome={isHome} data-testid="footer-support-link">
              Support
            </FooterLink>
          </div>
        </div>
        <div>
          <p className={twMerge("font-ui font-medium text-[13px] mb-2", mutedLabel)}>Follow us</p>
          <a
            className={twMerge("inline-flex items-center gap-2", actionClass)}
            href="https://instagram.com/rsvp.dance"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Instagram className="w-3.5 h-3.5" aria-hidden="true" /> @rsvp.dance
          </a>
        </div>
      </div>
      <div
        className={twMerge(
          "border-t py-4 text-center text-[12px] font-medium",
          isHome ? "border-cream/15 text-cream/45" : "border-subtle text-muted",
        )}
      >
        rsvp.dance — Simple RSVPs for the dance community.
      </div>
    </footer>
  );
}
